"use client";

import React from "react";
import { Navbar } from "@/components/ui/navbar";
import { Footer } from "@/components/ui/footer";
import {
	ThemeBackground,
	ThemeCard,
	ThemeText,
	AuroraBlobs,
} from "@/components/ui/theme-utils";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

export default function PrivacyPolicyPage() {
	const { isDark } = useTheme();

	return (
		<ThemeBackground className="min-h-screen flex flex-col">
			<Navbar />

			<main className="flex-grow">
				<section className="relative overflow-hidden pt-40 sm:pt-48 pb-12 sm:pb-16">
					<div
						className={cn(
							"absolute inset-0 -z-10",
							isDark ? "bg-mesh-dark" : "bg-mesh-light"
						)}
					/>
					<div
						className={cn(
							"absolute inset-0 -z-10 opacity-40",
							"bg-grid"
						)}
					/>
					<AuroraBlobs variant="mixed" />

					<div className="container relative z-10 mx-auto px-5 sm:px-6">
						<div className="mx-auto max-w-4xl">
							<div className="text-center mb-8">
								<h1 className="responsive-heading-2">
									Privacy <span className="text-gradient">Policy</span>
								</h1>
								<ThemeText variant="subtle" className="mt-6 text-lg sm:text-xl leading-relaxed">
									Last updated: 12-12-2025
								</ThemeText>
							</div>

							<ThemeCard className="p-6 sm:p-10">
								<div className="space-y-8">
									<ThemeText>
										Welcome to NOVARISE (“Zset”, “we”, “our”, or “us”). We are committed to protecting your privacy and ensuring
										that your personal information is handled in a safe and responsible manner. This Privacy Policy outlines how
										we collect, use, and protect your data when you use our cloud storage services and website.
									</ThemeText>

									<section>
										<h2 className="responsive-heading-3 mb-4">1. Information We Collect</h2>
										<ThemeText className="mb-4">
											We collect information to provide better services to all our users. The types of information we collect include:
										</ThemeText>
										<ul className="list-disc pl-6 space-y-2 marker:text-brand-500">
											<li>
												<ThemeText>
													<strong>Personal Information:</strong> Name, email address, phone number, and billing information when you
													create an account or subscribe to our services.
												</ThemeText>
											</li>
											<li>
												<ThemeText>
													<strong>Files and Data:</strong> The files, photos, documents, and other content you upload to our cloud storage service.
												</ThemeText>
											</li>
											<li>
												<ThemeText>
													<strong>Usage Data:</strong> Information about how you use our services, such as file sizes, time of access, and
													interaction with features.
												</ThemeText>
											</li>
											<li>
												<ThemeText>
													<strong>Device Information:</strong> Information about the device you use to access our services, including IP address,
													browser type, and operating system.
												</ThemeText>
											</li>
										</ul>
									</section>

									<section>
										<h2 className="responsive-heading-3 mb-4">2. How We Use Your Information</h2>
										<ThemeText className="mb-4">
											We use the information we collect for the following purposes:
										</ThemeText>
										<ul className="list-disc pl-6 space-y-2 marker:text-brand-500">
											<li>
												<ThemeText>To provide, maintain, and improve our services.</ThemeText>
											</li>
											<li>
												<ThemeText>To process transactions and manage your account.</ThemeText>
											</li>
											<li>
												<ThemeText>To communicate with you, including sending service updates, security alerts, and support messages.</ThemeText>
											</li>
											<li>
												<ThemeText>To ensure the security and integrity of our platform and prevent fraud.</ThemeText>
											</li>
										</ul>
									</section>

									<section>
										<h2 className="responsive-heading-3 mb-4">3. Data Security</h2>
										<ThemeText>
											We implement robust security measures to protect your data from unauthorized access, alteration, disclosure, or
											destruction. These measures include encryption of data in transit and at rest, regular security audits, and strict
											access controls. However, no method of transmission over the internet or electronic storage is 100% secure, and
											we cannot guarantee absolute security.
										</ThemeText>
									</section>

									<section>
										<h2 className="responsive-heading-3 mb-4">4. Sharing Your Information</h2>
										<ThemeText>
											We do not share your personal information or private files with third parties, except in the following circumstances:
										</ThemeText>
										<ul className="list-disc pl-6 space-y-2 marker:text-brand-500">
											<li>
												<ThemeText>With your consent.</ThemeText>
											</li>
											<li>
												<ThemeText>
													With service providers who assist us in operating our business (e.g., payment processors), subject to strict
													confidentiality agreements.
												</ThemeText>
											</li>
											<li>
												<ThemeText>
													If required by law, regulation, or legal process.
												</ThemeText>
											</li>
										</ul>
									</section>

									<section>
										<h2 className="responsive-heading-3 mb-4">5. Your Rights</h2>
										<ThemeText>
											Depending on your location, you may have the right to access, correct, delete, or export your personal data.
											You can manage your files and account settings directly through the service. For other requests, you may contact us.
										</ThemeText>
									</section>

									<section>
										<h2 className="responsive-heading-3 mb-4">6. Changes to This Policy</h2>
										<ThemeText>
											We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new
											policy on our website or sending you an email notification.
										</ThemeText>
									</section>

									<section>
										<h2 className="responsive-heading-3 mb-4">7. Contact Us</h2>
										<ThemeText>
											If you have any questions about this Privacy Policy, please contact us at:
										</ThemeText>
										<ThemeText className="mt-2 font-semibold">
											NOVARISE<br />
											Email: info@zset.in
										</ThemeText>
									</section>
								</div>
							</ThemeCard>
						</div>
					</div>
				</section>
			</main>

			<Footer isHomePage={false} />
		</ThemeBackground>
	);
}
